'use client';

import {
  Select as SelectPrimitive,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../tailwind/ui/select';
import { Checkbox } from '../../../tailwind/ui/checkbox';
import { PrimeTooltip } from '../../../prime-tooltip';
import { PRIME_TRACKING_ROUTES } from '../../../constants';
import { SelectProps, SelectOptions } from './interface';

export const SelectApp = ({
  options,
  children,
  selected = [],
  placeholder,
  multiple,
  onChange,
  value,
  ...props
}: SelectProps) => {
  const handleValueChange = (value: string) => {
    if (multiple) {
      // Handle multiple selection
      const currentSelected = selected || [];
      const newSelected = currentSelected.includes(value)
        ? currentSelected.filter((item) => item !== value)
        : [...currentSelected, value];

      if (onChange) {
        onChange({ target: { value: newSelected } } as any, null);
      }
    } else {
      // Handle single selection
      const selectedOption = options.find((option) => option.value === value);

      if (selectedOption?.exclusiveToPrime) return;

      if (onChange) {
        onChange({ target: { value } } as any, null);
      }
    }
  };

  const renderValue = (value: string | string[]) => {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      return placeholder || '';
    }

    const values = Array.isArray(value) ? value : [value];

    return values
      .map((val: string) => {
        const option = options.find((option) => option.value === val);
        return option?.label;
      })
      .filter(Boolean)
      .join(', ');
  };

  // For multiple selection, we need to handle it differently since shadcn/ui Select expects a single string value
  const currentValue = multiple ? '' : typeof value === 'string' ? value : '';
  const displayValue = renderValue(
    multiple ? (selected as string[]) : typeof value === 'string' ? value : ''
  );

  return (
    <SelectPrimitive
      value={currentValue}
      onValueChange={handleValueChange}
      {...props}
    >
      <SelectTrigger className="min-w-[120px] w-fit rounded-md">
        {props.startAdornment && (
          <span className="text-[#A0A0A0]">{props.startAdornment}</span>
        )}
        <SelectValue placeholder={displayValue || placeholder} />
      </SelectTrigger>

      <SelectContent className="max-h-[350px]">
        {multiple &&
          options?.map((option) => (
            <SelectItem
              key={`multi-${option.value}`}
              value={option.value as string}
              disabled={option.exclusiveToPrime}
            >
              <Checkbox
                checked={selected.indexOf(option.value as string) > -1}
                className="pointer-events-none"
              />
              <PrimeTooltip
                userIsPrime={!option.exclusiveToPrime}
                link={PRIME_TRACKING_ROUTES.CTAHistoricalProfitability('')}
              >
                <span className="break-words whitespace-normal w-full">
                  {option.label}
                </span>
              </PrimeTooltip>
            </SelectItem>
          ))}

        {!multiple &&
          options.map((option) => (
            <SelectItem
              key={`single-${option.value}`}
              value={option.value as string}
              disabled={option.exclusiveToPrime}
              style={{
                filter: option.exclusiveToPrime ? 'blur(1.5px)' : undefined,
              }}
            >
              {option.label}
            </SelectItem>
          ))}
      </SelectContent>
    </SelectPrimitive>
  );
};
